import styled from 'styled-components';
import { Colors, Icons, Typography } from '@replai-platform/ui-components';
import { NetworkConnectionGuideContent } from '../../types';
import * as Shared from '../shared';

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 85%;
  padding-top: 2rem;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  border: 1px solid ${Colors.Gray[200]};
  border-radius: 0.75rem;
  background-color: ${Colors.Common.White};
  box-shadow: rgba(149, 157, 165, 0.15) 0px 4px 12px;
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 6px solid ${Colors.Primary[50]};
  border-radius: 50%;
  background-color: ${Colors.Primary[100]};
  box-sizing: content-box;
`;

const Checklist = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ChecklistItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const StepNumber = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${Colors.Primary[600]};
  color: ${Colors.Common.White};
  font-weight: 600;
`;

const Callout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border: 1px solid ${Colors.Primary[200]};
  border-radius: 0.75rem;
  background-color: ${Colors.Primary[25]};
`;

const RequirementCard = ({
  icon,
  title,
  children,
}: {
  icon: Parameters<typeof Icons.getBaseIcon>[0];
  title: string;
  children: React.ReactNode;
}) => {
  const Icon = Icons.getBaseIcon(icon);
  return (
    <Card>
      <IconBadge>
        <Icon color={Colors.Primary[600]} dimension={20} />
      </IconBadge>
      <Typography type="text-md" fontWeight="semi-bold" noMargin>
        {title}
      </Typography>
      <Typography type="text-sm" color={Colors.Gray[600]} noMargin>
        {children}
      </Typography>
    </Card>
  );
};

const InfoCallout = ({ children }: { children: React.ReactNode }) => {
  const InfoIcon = Icons.getBaseIcon('Info');
  return (
    <Callout>
      <InfoIcon color={Colors.Primary[600]} dimension={20} />
      <Typography type="text-sm" color={Colors.Primary[800]} noMargin>
        {children}
      </Typography>
    </Callout>
  );
};

export const RemergeConnectionGuide: NetworkConnectionGuideContent = {
  title: 'Remerge Connection Guide',
  icon: 'Remerge',
  guide: {
    label: 'Connection Guide',
    steps: [
      {
        title: 'Prepare a dashboard user',
        description: 'What you need from Remerge',
        content: (
          <StepContainer>
            <Shared.RegularText>
              Replai connects to Remerge through a <Shared.BoldText>Remerge dashboard user</Shared.BoldText>. Before
              moving on, make sure you have one ready:
            </Shared.RegularText>
            <Cards>
              <RequirementCard icon="UserPlus" title="Dedicated user">
                We recommend a user created just for Replai, so you can revoke its access at any time without affecting
                your team.
              </RequirementCard>
              <RequirementCard icon="Users" title="Audience access">
                The user must be able to see every audience you want to analyse with Replai.
              </RequirementCard>
              <RequirementCard icon="Key" title="Login credentials">
                Keep the user&apos;s email and password at hand. You&apos;ll share them in the next step.
              </RequirementCard>
            </Cards>
            <InfoCallout>
              Can&apos;t create users yourself? Ask your <Shared.BoldText>Remerge account manager</Shared.BoldText> to
              set one up for you.
            </InfoCallout>
          </StepContainer>
        ),
      },
      {
        title: 'Share access with Replai',
        description: 'Credentials and audiences',
        content: (
          <StepContainer>
            <Checklist>
              <ChecklistItem>
                <StepNumber>1</StepNumber>
                <Shared.RegularText>
                  Send your <Shared.BoldText>Replai point of contact</Shared.BoldText> the dashboard user&apos;s{' '}
                  <Shared.BoldText>email</Shared.BoldText> and <Shared.BoldText>password</Shared.BoldText>.
                </Shared.RegularText>
              </ChecklistItem>
              <ChecklistItem>
                <StepNumber>2</StepNumber>
                <Shared.RegularText>
                  Tell us which <Shared.BoldText>audiences</Shared.BoldText> you want to connect.
                </Shared.RegularText>
              </ChecklistItem>
              <ChecklistItem>
                <StepNumber>3</StepNumber>
                <Shared.RegularText>
                  We&apos;ll connect the account. Once it&apos;s done, it appears in the{' '}
                  <Shared.BoldText>Connected Accounts</Shared.BoldText> tab with its title and audience.
                </Shared.RegularText>
              </ChecklistItem>
            </Checklist>
            <InfoCallout>
              If you change the user&apos;s password or remove the user, let your Replai point of contact know so your
              data keeps flowing.
            </InfoCallout>
          </StepContainer>
        ),
      },
    ],
  },
  faqs: {
    label: 'FAQ',
    content: Shared.DEFAULT_NETWORK_FAQS,
  },
};
